import { Component, Input, OnDestroy } from '@angular/core';
import { CardComponent } from 'components/layout/card/card.component';
import { ProductPlanTagConstants } from 'constants/ProductPlanTagConstants';
import { CredentialType } from 'enums/CredentialType';
import { SiteType } from 'enums/SiteType';
import 'extensions/validation.ts';
import { SiteModel, SiteStatus } from 'models/generated/Distribution.Api.Models.Site';
import { ProductPlanModel } from 'models/generated/Entities.GormCrm.ProductPlan';
import { Layout } from 'modules/layout';
import { TranslatePipe } from 'pipes/translate.pipe';
import { CompanyDataProvider, CompanyDataProviderFactory } from 'services/company-data-provider';
import { DateService } from 'services/date.service';
import { DnsPointerService } from 'services/dns-lookup.service';
import { NotificationService } from 'services/notification.service';
import { SiteService } from 'services/site.service';
import { AddCompanySiteComponent } from './add-company-site.component';
import { WebsiteDetailsComponent } from './website-details.component';

@Component({
	selector: 'company-sites',
	templateUrl: './company-sites.component.html',
	providers: [DnsPointerService],
	imports: [Layout, CardComponent, WebsiteDetailsComponent, AddCompanySiteComponent, TranslatePipe]
})
export class CompanySitesComponent implements OnDestroy {

	dataProvider: CompanyDataProvider;
	CredentialType = CredentialType;
	sites: SiteModel[] = [];
	working = false;
	webProductPlans: ProductPlanModel[] = [];
	loading = true;
	
	private _companyId?: string;
	private sitePollingTimeout?: NodeJS.Timeout;
	@Input()
	set companyId(id: string | undefined) {
		this._companyId = id;
		if (!this.companyId) return;

		Promise.all([
			this.getCompanySites(),
			this.dataProvider.getProductPlansByTag(ProductPlanTagConstants.web).then(webPlans => {
				this.webProductPlans = webPlans;
			})
		]).then(() => {
			this.loading = false;
		});
	}
	get companyId() {
		return this._companyId;
	}

	siteProductPlan(site: SiteModel) {
		return this.webProductPlans.find(p => p.id === site.productPlanId);
	}

	constructor(public readonly siteService: SiteService,
		dataProviderFactory: CompanyDataProviderFactory,
		private readonly notificationService: NotificationService,
		private readonly dateService: DateService
	) {
		this.dataProvider = dataProviderFactory.getProviderForCurrentScope();
	}

	async getCompanySites(refreshCache: boolean = false) {
		if (this.companyId == undefined) return;

		if (this.sitePollingTimeout) {
			clearTimeout(this.sitePollingTimeout);
			this.sitePollingTimeout = undefined;
		}

		this.sites = await this.dataProvider.getSites(refreshCache);

		if (this.sites.some(s => s.type === SiteType.WordPressProvisioningApi && s.status === SiteStatus.Working)) {
			// If there is a working WordPress site, check for updates after 5 seconds
			this.sitePollingTimeout = setTimeout(() => this.getCompanySites(true), 5000);
		}
	}

	public getSiteId(index: number, site: SiteModel) {
		return site.id;
	}

	async createRedesign(site: SiteModel) {
		if (this.working) return;
		this.working = true;
		await this.siteService.createRedesign(site.id!);
		this.working = false;
	}

	async activateRedesign(site: SiteModel) {
		if (this.working) return;
		this.working = true;
		await this.siteService.activateRedesign(site.id!);
		this.working = false;
	}

	deleteSite(site: SiteModel) {
		if (this.working) return;
		this.notificationService.confirm('site.delete-confirmation', { 'siteName': this.siteService.getSiteName(site) }).subscribe(async result => {
			if (result) this.forceDeleteSite(site);
		});
	}

	autoDeleteSite(site: SiteModel) {
		if (this.working) return;
		this.notificationService.confirm('site.auto-delete-confirmation', { 'siteName': this.siteService.getSiteName(site) }).subscribe(async result => {
			if (result) {
				this.working = true;
				await this.siteService.autoDeleteSite(site.id!);
				await this.getCompanySites();
				this.working = false;
			}
		});
	}

	private async forceDeleteSite(site: SiteModel) {
		if (this.working) return;
		this.working = true;
		await this.siteService.deleteSite(site.id!);
		this.working = false;
	}

	async cancelAutoDelete(site: SiteModel) {
		let productPlan = this.siteProductPlan(site);
		if (productPlan?.autoRenewalDate && this.dateService.getDate(productPlan?.autoRenewalDate) < this.dateService.today()) {
			this.notificationService.warning('site.cancel-auto-delete-not-possible.not-attached-active-product-plan');
			return;
		}
		if (this.working) return;
		this.working = true;
		await this.siteService.cancelAutoDelete(site.id!);
		this.working = false;
	}

	async addSsl(site: SiteModel, validPointers: string[]) {
		if (this.working) return;
		if (validPointers.length == 0) {
			this.notificationService.warning('site.ssl.no-valid-pointers');
			return;
		}
		const primaryPointer = this.siteService.getSitePrimaryPointer(site);
		if (!primaryPointer) {
			this.notificationService.warning('site.ssl.primary-pointer-missing');
			return;
		}
		if (!validPointers.includes(primaryPointer)) {
			this.notificationService.warning('site.ssl.primary-pointer-not-valid');
			return;
		}
		this.working = true;
		await this.siteService.addSsl(site.id!, validPointers);
		this.working = false;
	}

	ngOnDestroy(): void {
		if (this.sitePollingTimeout) clearTimeout(this.sitePollingTimeout);
	}
}
